import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { BACKGROUND_REQUEST, HANDLES_CONFLICT } from './http-context';

/** Shape of a NestJS HttpException's JSON body. */
interface ApiErrorBody {
  statusCode?: number;
  error?: string;
  message?: string;
}

// Structural, not `instanceof HttpErrorResponse`: the retry path's error can be
// a plain `{ error }`-shaped object (e.g. AuthService.refresh() test doubles),
// not just a real HttpErrorResponse from the HTTP client.
const getApiErrorBody = (error: unknown): ApiErrorBody | undefined =>
  (error as { error?: ApiErrorBody } | null | undefined)?.error;

/**
 * A failed download (`responseType: 'blob'`) carries its JSON error as a Blob,
 * which the handlers below cannot read. Opening it first lets the user see the
 * server's own message ("choose a shorter period") instead of "Unknown Error".
 */
const readBlobError = (error: unknown): Observable<never> => {
  if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob)) {
    return throwError(() => error);
  }
  return from(error.error.text()).pipe(
    switchMap((text) => {
      let body: unknown;
      try {
        body = JSON.parse(text);
      } catch {
        body = undefined;
      }
      return throwError(
        () =>
          new HttpErrorResponse({
            error: body,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          }),
      );
    }),
  );
};

export const queryInterceptor: HttpInterceptorFn = (req, next) => {
  const notifService = inject(NzNotificationService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const clonedReq = req.clone({ withCredentials: true });
  // Taken before the request goes out: if it 401s, refresh() only skips when
  // the session has moved on since this moment.
  const sessionStamp = authService.expiryStamp();

  const isLoginRoute = clonedReq.url.endsWith('/auth/login');
  const isRefreshRoute = clonedReq.url.endsWith('/auth/refresh');

  const reportGenericError = (errObj: ApiErrorBody | undefined) => {
    if (errObj?.error) {
      notifService.error(errObj.error, errObj.message ?? '');
    } else {
      notifService.error('Unknown Error', 'Please contact support');
    }
  };

  const isBackground = clonedReq.context.get(BACKGROUND_REQUEST);

  // Status-code-aware reporting for an ordinary business request.
  const reportError = (errObj: ApiErrorBody | undefined) => {
    if (isBackground && errObj?.statusCode !== 401) {
      return;
    }
    switch (errObj?.statusCode) {
      case 400:
        notifService.error(errObj.error ?? 'Bad Request', errObj.message ?? '');
        break;
      case 401:
        notifService.error('Your session expired', 'Please login again');
        authService.onSessionConfirmedDead();
        void router.navigate(['/login']);
        break;
      case 404:
        notifService.error(
          'Resource Not Found',
          'Looks like the data you try to get does not exist',
        );
        break;
      case 429:
        notifService.error('Too Many Requests', 'Try again later');
        break;
      case 500:
        notifService.error('An error occured', 'Try again latter or contact support');
        break;
      default:
        reportGenericError(errObj);
        break;
    }
  };

  if (isLoginRoute) {
    // Never reported here: the login page shows a failed sign-in inline, next
    // to the form, and there is no session yet to expire.
    return next(clonedReq);
  }

  if (isRefreshRoute) {
    // Never reported here. This request only ever runs nested inside
    // another request's own catchError below (or the AuthService leader
    // timer, which reports nothing on purpose) — reporting it again here
    // would show the same "session expired" toast twice for one failure.
    return next(clonedReq);
  }

  return next(clonedReq).pipe(
    catchError(readBlobError),
    catchError((error: unknown) => {
      const errObj = getApiErrorBody(error);
      if (errObj?.statusCode === 401) {
        return authService.refresh(sessionStamp).pipe(
          switchMap(() => next(clonedReq).pipe(catchError(readBlobError))),
          // Whatever comes back from a failed refresh, or from retrying
          // this request after a successful one, is judged on its own
          // merits — only a genuine 401/403 means the session is actually
          // dead. A 429/5xx/network hiccup fails just this one request; it
          // does not sign the user out.
          catchError((retryError: unknown) => {
            reportError(getApiErrorBody(retryError));
            return throwError(() => retryError);
          }),
        );
      }
      const handledConflict = errObj?.statusCode === 409 && clonedReq.context.get(HANDLES_CONFLICT);
      if (!handledConflict) {
        reportError(errObj);
      }
      return throwError(() => error);
    }),
  );
};
